import { Lightning } from "@lightningjs/sdk";
import { PlayerProgress } from "./components/PlayerProgress";
import { PlayPauseButton } from "./components/PlayPauseButton";
import { SettingButton } from "./components/SettingButton";
import theme from "../../../../lib/theme";
import { formatTime } from "../../../../lib/utils";

interface PlayerBottomColumnTemplateSpec extends Lightning.Component.TemplateSpec {
    PlayPause: typeof PlayPauseButton;
    CurrentTime: object;
    Duration: object;
    Progress: typeof PlayerProgress;
    SettingIcon: typeof SettingButton;
    duration: number;
    currentTime: number;
}

export class PlayerBottomColumn
    extends Lightning.Component<PlayerBottomColumnTemplateSpec>
    implements Lightning.Component.ImplementTemplateSpec<PlayerBottomColumnTemplateSpec>
{
    _duration = 0;
    private _hasSettings = false; // true means settings ARE available, false means NOT available

    readonly PlayPause = this.getByRef("PlayPause")!;
    readonly CurrentTime = this.getByRef("CurrentTime")!;
    readonly Duration = this.getByRef("Duration")!;
    readonly Progress = this.getByRef("Progress")!;
    readonly SettingIcon = this.getByRef("SettingIcon")!;

    setHasSettings(hasSettings: boolean) {
        this._hasSettings = hasSettings; // true means settings ARE available
    }

    $setFocusToSettingIcon() {
        if (this._hasSettings) {
            this._setState("SettingState");
        }
    }

    static override _template(): Lightning.Component.Template<PlayerBottomColumnTemplateSpec> {
        return {
            w: theme.layout.w - 72 * 2,
            x: 72,
            flex: { direction: "row", alignItems: "center", justifyContent: "space-between" },
            PlayPause: {
                type: PlayPauseButton,
                signals: {
                    handleHover: "setPlayPauseState"
                }
            },
            CurrentTime: {
                y: 1,
                w: 100,
                flexItem: { marginLeft: 24 },
                text: {
                    text: "",
                    fontSize: 24,
                    fontFace: "Secondary-Regular"
                }
            },
            Progress: {
                w: theme.layout.w - theme.progress.offset * 2,
                h: 8,
                type: PlayerProgress
            },
            Duration: {
                y: 1,
                w: 100,
                text: {
                    text: "",
                    fontSize: 24,
                    fontFace: "Secondary-Regular",
                    textAlign: "right"
                }
            },
            SettingIcon: {
                y: -50,
                w: 43,
                h: 43,
                alpha: 0,
                flexItem: { marginLeft: 24 },
                type: SettingButton,
                signals: {
                    handleClick: "$showSettingsPopup",
                    setPlaybackSpeed: "$setPlaybackSpeed",
                    setFocusToSettingIcon: "$setFocusToSettingIcon"
                }
            }
        };
    }

    static override _states() {
        return [
            class PlayPauseState extends this {},
            class SettingState extends this {
                override _getFocused() {
                    return this.SettingIcon as any;
                }

                override _handleLeft() {
                    // Column layout: Left triggers video rewind
                    this.fireAncestors("$triggerVideoRewind");
                }

                override _handleRight() {
                    // Column layout: Right triggers video forward
                    this.fireAncestors("$triggerVideoForward");
                }

                $setPlaybackSpeed(value: number) {
                    this.fireAncestors("$setPlaybackSpeed", value);
                }

                $showSettingsPopup() {
                    this.fireAncestors("$showSettingsPopup");
                }

                override _handleEnter() {
                    this.fireAncestors("$showSettingsPopup");
                }

                override _handleDown() {
                    this._setState("");
                }

                override _handleUp() {
                    return false;
                }
            }
        ];
    }

    override _getFocused() {
        return this.PlayPause;
    }

    override _handleUp() {
        // Column layout: Up focuses Settings
        if (this._hasSettings) {
            this._setState("SettingState");
        }
    }

    override _active() {
        this._setState("");

        if (window.selectedMovie?.__typename === "LiveStream") {
            this.PlayPause.patch({
                alpha: 0
            });
            this.CurrentTime.patch({
                alpha: 0
            });
            this.Progress.patch({
                alpha: 0
            });
            this.Duration.patch({
                alpha: 0
            });
            this.SettingIcon.patch({
                alpha: 0
            });
        }
    }

    getSettingIcon() {
        return this.SettingIcon;
    }

    hideSettingIcon() {
        this.SettingIcon.patch({
            alpha: 0
        });
    }

    showSettingIcon() {
        this.SettingIcon.patch({
            alpha: 1
        });
    }

    triggeredPlayPause() {
        this.PlayPause.triggeredPlayPause();
    }

    triggeredPause() {
        this.PlayPause.triggeredPause();
    }

    triggeredPlay() {
        this.PlayPause.triggeredPlay();
    }

    setPlayState(value: boolean) {
        this.PlayPause.setPlayState(value);
    }

    setRewindState() {
        this._setState("");
    }

    setPlayPauseState() {
        this._setState("");
    }

    setForwardState() {
        this._setState("");
    }

    override _handleRight() {
        this.fireAncestors("$triggerVideoForward");
    }

    override _handleLeft() {
        this.fireAncestors("$triggerVideoRewind");
    }

    override _handleEnter(): boolean | void {
        return false;
    }

    set duration(value: number) {
        this._duration = value;
        this.Duration.patch({
            text: {
                text: formatTime(value)
            }
        });
    }

    set currentTime(value: number) {
        this.CurrentTime.patch({
            text: {
                text: formatTime(value, {
                    hideEmpty: false,
                    useSeconds: true
                })
            }
        });

        if (this._duration) {
            this.Progress.patch({
                progress: value ? value / this._duration : 0
            });
        }
    }
}
